/**
 * Помесячный свод затрат по статьям (ADR-071 D9) — аналог «Свод ТК (БПУ)».
 *
 * Две подачи (ADR-070): «к урожаю» — все работы под этот урожай, включая
 * осень прошлого календарного года (НЗП); «деньги года» — затраты
 * календарного года сезона: без входящего НЗП, но с осенними работами под
 * будущий урожай. Мостик «деньги года = к урожаю − входящее НЗП + осень под
 * следующий» держит бэк — здесь только показываем его словами.
 */
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ARTICLE_TITLES, formatMoney } from '@/lib/planning/techCards';
import {
  monthLabel,
  nonEmptyRows,
  useMonthlyReport,
  visibleMonths,
  type MonthlyBasis,
  type ReportScope,
} from '@/lib/planning/techCardReports';

const BASIS_TITLES: Record<MonthlyBasis, string> = {
  harvest: 'К урожаю',
  cash: 'Деньги года',
};

export function MonthlyReportBlock({ scope }: { scope: ReportScope }) {
  const [basis, setBasis] = useState<MonthlyBasis>('harvest');
  const report = useMonthlyReport(scope, basis);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-foreground">Затраты по месяцам</h3>
          <div className="ml-auto flex flex-wrap gap-2">
            {(Object.keys(BASIS_TITLES) as MonthlyBasis[]).map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={basis === key}
                onClick={() => setBasis(key)}
                className={
                  basis === key
                    ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
                    : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                }
              >
                {BASIS_TITLES[key]}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
          {basis === 'harvest'
            ? 'Все работы под этот урожай, включая осень прошлого календарного года. Сумма размазана по месяцам агроокна пропорционально календарным дням.'
            : 'Затраты календарного года сезона: без осени прошлого года, но с осенними работами под будущий урожай. Это календарь затрат, не платёжный календарь.'}
        </p>

        {report.isLoading && <Skeleton className="mt-3 h-40 w-full" />}
        {report.isError && (
          <p className="mt-3 text-sm text-destructive">
            Не удалось посчитать свод по месяцам — обновите страницу.
          </p>
        )}

        {report.data && <MonthlyTable data={report.data} />}
      </CardContent>
    </Card>
  );
}

function MonthlyTable({ data }: { data: NonNullable<ReturnType<typeof useMonthlyReport>['data']> }) {
  const rows = nonEmptyRows(data.rows);
  const months = visibleMonths(rows);
  const unscheduledTotal = rows.reduce((acc, row) => acc + row.unscheduled, 0);

  if (rows.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        Денег в этой подаче нет — либо в картах нет посчитанных строк, либо все они
        относятся к другой подаче.
      </p>
    );
  }

  return (
    <div className="mt-3 overflow-x-auto">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="text-xs">
            <TableHead>Статья</TableHead>
            {months.map((month) => (
              <TableHead key={month} className="text-right whitespace-nowrap">
                {monthLabel(month)}
              </TableHead>
            ))}
            {unscheduledTotal !== 0 && <TableHead className="text-right">Без срока</TableHead>}
            <TableHead className="text-right">Итого</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.article}>
              <TableCell className="py-2 text-muted-foreground whitespace-nowrap">
                {ARTICLE_TITLES[row.article]}
              </TableCell>
              {months.map((month) => (
                <TableCell key={month} className="py-2 text-right whitespace-nowrap">
                  {row.months[month] ? formatMoney(row.months[month]) : ''}
                </TableCell>
              ))}
              {unscheduledTotal !== 0 && (
                <TableCell className="py-2 text-right whitespace-nowrap" title="Строки без агроокна">
                  {row.unscheduled ? formatMoney(row.unscheduled) : ''}
                </TableCell>
              )}
              <TableCell className="py-2 text-right font-medium whitespace-nowrap">
                {formatMoney(row.total)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="font-semibold">
            <TableCell className="py-2">Итого</TableCell>
            {months.map((month) => (
              <TableCell key={month} className="py-2 text-right whitespace-nowrap">
                {data.month_totals[month] ? formatMoney(data.month_totals[month]) : ''}
              </TableCell>
            ))}
            {unscheduledTotal !== 0 && (
              <TableCell className="py-2 text-right whitespace-nowrap">
                {formatMoney(unscheduledTotal)}
              </TableCell>
            )}
            <TableCell className="py-2 text-right whitespace-nowrap">
              {formatMoney(data.total)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {(data.wip.incoming > 0 || data.wip.outgoing != null) && (
        <p className="mt-2 text-xs text-muted-foreground">
          НЗП: осень прошлого года под этот урожай — {formatMoney(data.wip.incoming)}
          {data.wip.outgoing != null &&
            ` · осень этого года под будущий урожай — ${formatMoney(data.wip.outgoing)}`}
          {data.wip.outgoing_status === 'next_season_not_planned' &&
            ' · план следующего сезона ещё не открыт, осенних работ под него честно нет'}
          {data.wip.outgoing_status === 'next_season_draft' &&
            data.wip.outgoing_source &&
            ` · по черновику «${data.wip.outgoing_source.name}» — он ещё меняется`}
        </p>
      )}

      {data.gaps.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-6 text-xs text-muted-foreground">
          {data.gaps.slice(0, 5).map((gap) => (
            <li key={gap}>{gap}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
